import { TableContext } from "../context";
import React, { useState, useContext, useEffect, useMemo } from "react";
import "../App.css";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { DateTime } from 'luxon';
import axios from "axios";
import { toast } from "react-toastify";
import {
    getUniqueColumnValues,
    applyColumnFilters,
    applyGlobalSearch,
    applyAdvancedFilters,
    transformIncidentForDisplay,
    transformIncidentForCSV,
} from "./P1P2IncidentsTable.helpers";
import {
    McTable,
    McButton,
    McSelect,
    McOption,
    McPagination
} from "@maersk-global/mds-react-wrapper";
import { McDrawer } from "@maersk-global/mds-react-wrapper/components-core/mc-drawer";
import { McInput } from "@maersk-global/mds-react-wrapper/components-core/mc-input";
import { McTextarea } from "@maersk-global/mds-react-wrapper/components-core/mc-textarea";
import { McPopover } from "@maersk-global/mds-react-wrapper/components-core/mc-popover";
import { McCheckboxGroup } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox-group";
import { McCheckbox } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox";
import FilterButton from "./FilterButton";

/**
 * P1P2IncidentsTable component for displaying and filtering P1/P2 incident data.
 */
export default function P1P2IncidentsTable(props) {
    const { p1p2IncidentsData, search, searchType, activeP1P2Tab, getP1P2IncidentsData, graphData } = useContext(TableContext);
    const [headingText, setHeadingText] = useState('P1/P2 Incidents'); 
    const [activeFilters, setActiveFilters] = useState([]);
    const [tableStyles, setTableStyles] = useState();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 75; // Number of records per page

    // Column header filters state
    const [columnFilters, setColumnFilters] = useState({});

    // Row selection and edit drawer state
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Form state for editing
    const [formData, setFormData] = useState({
        applicationId: "",
        configurationItem: "",
        shortDescription: "",
        priority: "",
        mvc: "",
        platform: "",
        portfolio: "",
        causedByPlatform: "",
        causedByPortfolio: "",
        brand: "",
        causedByChange: "",
        changeType: "",
        changeProcess: "",
        triggeringChangeRequest: "",
        observations: "",
        rootCauseCode: "",
        rootCauseSubCode: ""
    });

    // Store the incident ID being edited to ensure proper data sync
    const [editingIncidentId, setEditingIncidentId] = useState(null);

    // Loading state for save operation
    const [isSaving, setIsSaving] = useState(false);

    // Define base table columns
    const baseTableColumns = [
        {
            id: "incidentNumber",
            label: props.Header1 || "Incident Number",
            sortable: true,
        },
        {
            id: "applicationId",
            label: props.Header2 || "Application ID",
            sortable: true,
        },
        {
            id: "configurationItem",
            label: props.Header3 || "Configuration Item",
            sortable: true,
            width: "5vw"
        },
        {
            id: "shortDescription",
            label: props.Header4 || "Short Description",
            sortable: true,
            nowrap: false,
        },
        {
            id: "resolved",
            label: props.Header5 || "Resolved At",
            sortable: true,
        },
        {
            id: "opened",
            label: props.Header6 || "Opened At",
            sortable: true,
        },
        {
            id: "priority",
            label: props.Header7 || "Priority",
            sortable: true,
        },
        {
            id: "brand",
            label: props.Header8 || "Brand",
            sortable: true,
        },
        {
            id: "mvc",
            label: props.Header9 || "MVC",
            sortable: true,
        },
        {
            id: "platform",
            label: props.Header10 || "Platform",
            sortable: true,
        },
        {
            id: "portfolio",
            label: props.Header11 || "Portfolio",
            sortable: true,
        },
        {
            id: "causedByPlatform",
            label: props.Header12 || "Caused By Platform",
            sortable: true,
        },
        {
            id: "causedByPortfolio",
            label: props.Header13 || "Caused By Portfolio",
            sortable: true,
        },
        {
            id: "causedByChange",
            label: props.Header14 || "Caused By Change",
            sortable: true,
        },
    ];

    // Additional columns for 'P1/P2 due to change' tab
    const additionalColumns = [
        {
            id: "changeType",
            label: props.Header15 || "Change Type",
            sortable: true,
        },
        {
            id: "changeProcess",
            label: props.Header16 || "Change Process",
            sortable: true,
        },
        {
            id: "triggeringChangeRequest",
            label: props.Header17 || "Triggering Change Request",
            sortable: true,
        },
        {
            id: "observations",
            label: props.Header18 || "Observations",
            sortable: true,
            nowrap: false,
            width: "20vw",
        },
        {
            id: "rootCauseCode",
            label: props.Header19 || "Root Cause Code",
            sortable: true,
        },
        {
            id: "rootCauseSubCode",
            label: props.Header20 || "Root Cause Sub Code",
            sortable: true,
        },
    ];

    // Conditionally combine columns based on active tab
    const tableColumns = activeP1P2Tab === "P1/P2 due to change" 
        ? [...baseTableColumns, ...additionalColumns]
        : baseTableColumns;

    // Filter columns configuration - all table columns except opened and resolved
    const baseFilterColumns = [
        { id: 'incidentId', label: 'Incident ID' },
        { id: 'application_id', label: 'Application ID' },
        { id: 'configuration_item', label: 'Configuration Item' },
        { id: 'short_description', label: 'Short Description' },
        { id: 'priority', label: 'Priority' },
        { id: 'brand', label: 'Brand' },
        { id: 'mvc', label: 'MVC' },
        { id: 'platform', label: 'Platform' },
        { id: 'portfolio', label: 'Portfolio' },
        { id: 'caused_by_platform', label: 'Caused By Platform' },
        { id: 'caused_by_portfolio', label: 'Caused By Portfolio' },
        { id: 'caused_by_change', label: 'Caused By Change' },
    ];

    // Additional filter columns for 'P1/P2 due to change' tab
    const additionalFilterColumns = [
        { id: 'change_type', label: 'Change Type' },
        { id: 'change_process', label: 'Change Process' },
        { id: 'triggering_change_request', label: 'Triggering Change Request' },
        { id: 'observations', label: 'Observations' },
        { id: 'root_cause_code', label: 'Root Cause Code' },
        { id: 'root_cause_sub_code', label: 'Root Cause Sub Code' },
    ];

    // Conditionally combine filter columns based on active tab
    const filterColumns = activeP1P2Tab === "P1/P2 due to change" 
        ? [...baseFilterColumns, ...additionalFilterColumns]
        : baseFilterColumns;

    /**
     * Handle column filter change
     */
    const handleColumnFilterChange = (columnId, selectedValues) => {
        setColumnFilters(prev => {
            const newFilters = { ...prev };
            // Ensure selectedValues is an array
            const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];
            if (valuesArray.length === 0) {
                delete newFilters[columnId];
            } else {
                newFilters[columnId] = valuesArray;
            }
            return newFilters;
        });
    };

    const filteredData = useMemo(() => {
        if (!p1p2IncidentsData) return [];

        // First, filter to show only resolved incidents (resolved is not null and not blank)
        let resolvedFiltered = p1p2IncidentsData.filter(incident => 
            incident.resolved && incident.resolved.trim() !== ""
        );

        // Then, filter by tab selection (client-side filtering)
        let tabFiltered = resolvedFiltered;
        if (activeP1P2Tab === "P1/P2 due to change") {
            // Show only incidents where caused_by_change is true
            tabFiltered = resolvedFiltered.filter(incident => incident.caused_by_change === true);
        }
        // For "All P1/P2 Incidents" tab, show all data (no filtering)

        // Apply filters in sequence
        let filtered = applyGlobalSearch(tabFiltered, search, searchType);
        filtered = applyAdvancedFilters(filtered, activeFilters);
        filtered = applyColumnFilters(filtered, columnFilters);

        // Sort by opened date (most recent first)
        filtered.sort((a, b) => {
            const dateA = a.opened ? new Date(a.opened) : new Date(0);
            const dateB = b.opened ? new Date(b.opened) : new Date(0);
            if (dateA < dateB) return 1;
            if (dateA > dateB) return -1;
            return 0;
        });

        return filtered;
    }, [p1p2IncidentsData, activeFilters, search, searchType, columnFilters, activeP1P2Tab]);

    // Client-side pagination - slice data before transformation
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

    // Display paginated data
    const transformedData = useMemo(() => 
        paginatedData.map(incident => transformIncidentForDisplay(incident, activeP1P2Tab)),
        [paginatedData, activeP1P2Tab]
    );

    // Update heading text based on active tab
    useEffect(() => {
        setHeadingText(activeP1P2Tab);
        // Reset to page 1 when tab changes
        setCurrentPage(1);
    }, [activeP1P2Tab]);

    // Handle pagination
    const handlePageChange = (event) => {
        setCurrentPage(event.detail);
    };

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    useEffect(() => {
        setTableStyles(`
            .mds-table {
                max-height: 65vh !important;
            }
        `);
    }, [transformedData]);

    const handleFilterApply = (filters) => {
        setActiveFilters(filters);
    };

    /**
     * Handle row selection change - enforce single selection
     */
    const handleSelectChange = (event) => {
        const newSelection = event.detail || [];
        
        // Enforce single selection: keep only the most recently selected row
        if (newSelection.length > 1) {
            // Find the newly selected row (the one not in previous selection)
            const newlySelected = newSelection.find(
                row => !selectedRows.some(prevRow => prevRow.incidentNumber === row.incidentNumber)
            );
            // Keep only the newly selected row
            setSelectedRows(newlySelected ? [newlySelected] : [newSelection[newSelection.length - 1]]);
        } else {
            setSelectedRows(newSelection);
        }
    };

    /**
     * Open edit drawer and populate form with current row data
     */
    const handleEditClick = () => {
        if (!selectedRows || selectedRows.length === 0 ) return;

        // Use the first selected row for editing
        const selectedRow = selectedRows[0];

        // Find the original incident data from the actual source data (not filtered)
        const incident = p1p2IncidentsData.find(inc => 
            inc.id === selectedRow.incidentNumber
        );

        if (incident) {
            // Convert triggering_change_request array to comma-separated string for display
            let triggeringChangeRequestValue = "";
            if (incident.triggering_change_request) {
                if (Array.isArray(incident.triggering_change_request)) {
                    triggeringChangeRequestValue = incident.triggering_change_request.join(", ");
                } else {
                    triggeringChangeRequestValue = incident.triggering_change_request;
                }
            }

            const newFormData = {
                applicationId: incident.application_id || "",
                configurationItem: incident.configuration_item || "",
                shortDescription: incident.short_description || "",
                priority: incident.priority || "",
                mvc: incident.mvc === null ? "" : (incident.mvc ? "Yes" : "No"),
                platform: incident.platform || "",
                portfolio: incident.portfolio || "",
                causedByPlatform: incident.caused_by_platform || "",
                causedByPortfolio: incident.caused_by_portfolio || "",
                brand: incident.brand || "",
                causedByChange: incident.caused_by_change === null ? "" : (incident.caused_by_change ? "Yes" : "No"),
                changeType: incident.change_type || "",
                changeProcess: incident.change_process || "",
                triggeringChangeRequest: triggeringChangeRequestValue,
                observations: incident.observations || "",
                rootCauseCode: incident.root_cause_code || "",
                rootCauseSubCode: incident.root_cause_sub_code || ""
            };
            setFormData(newFormData);
            setEditingIncidentId(incident.id);
            setIsDrawerOpen(true);
        }
    };

    /**
     * Handle form input changes
     */
    const handleFormChange = (field, value) => {
        // When causedByChange is set to "No" in P1/P2 due to change tab, reset other fields
        if (field === 'causedByChange' && value === 'No' && activeP1P2Tab === 'P1/P2 due to change') {
            setFormData(prev => ({
                ...prev,
                causedByChange: value,
                changeType: "",
                changeProcess: "",
                triggeringChangeRequest: "CHGXXXXXXX",
                observations: "",
                rootCauseCode: "",
                rootCauseSubCode: ""
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    /**
     * Save edited data to backend
     */
    const handleSave = async () => {
        if (!editingIncidentId) return;

        setIsSaving(true);

        try {
            const incidentId = editingIncidentId;
            
            // Prepare data with all editable fields
            let updateData = {
                application_id: formData.applicationId,
                configuration_item: formData.configurationItem,
                short_description: formData.shortDescription,
                priority: formData.priority,
                mvc: formData.mvc === "Yes" ? true : (formData.mvc === "No" ? false : null),
                platform: formData.platform,
                portfolio: formData.portfolio,
                caused_by_platform: formData.causedByPlatform,
                caused_by_portfolio: formData.causedByPortfolio,
                brand: formData.brand,
                caused_by_change: formData.causedByChange === "Yes" ? true : (formData.causedByChange === "No" ? false : null)
            };

            // Add additional fields for 'P1/P2 due to change' tab
            if (activeP1P2Tab === "P1/P2 due to change") {
                // Convert comma-separated values to array for triggering_change_request
                const triggeringChangeRequestArray = formData.triggeringChangeRequest
                    .split(',')
                    .map(val => val.trim())
                    .filter(val => val !== '');

                // Only add fields if they have values
                if (formData.changeType) {
                    updateData.change_type = formData.changeType;
                }
                if (formData.changeProcess) {
                    updateData.change_process = formData.changeProcess;
                }
                if (triggeringChangeRequestArray.length > 0) {
                    updateData.triggering_change_request = triggeringChangeRequestArray;
                }
                if (formData.observations) {
                    updateData.observations = formData.observations;
                }
                if (formData.rootCauseCode) {
                    updateData.root_cause_code = formData.rootCauseCode;
                }
                if (formData.rootCauseSubCode) {
                    updateData.root_cause_sub_code = formData.rootCauseSubCode;
                }
            }

            // Log the data being sent for debugging
            console.log("Sending update data:", updateData);

            // Make PUT request with user_email as query parameter
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URI}/api/change_data/p1p2Incidents/${incidentId}?user_email=${encodeURIComponent(graphData?.mail || '')}`,
                updateData,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                toast.success("Incident updated successfully", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                
                // Close drawer and reset selection
                setIsDrawerOpen(false);
                setEditingIncidentId(null);
                setSelectedRows([]);
                
                // Refetch the data from API
                await getP1P2IncidentsData();

                setIsSaving(false);
            }
        } catch (error) {
            console.error("Error updating incident:", error);
            toast.error(`Failed to update incident: ${error.response?.data?.detail || error.message}`, {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });

            setIsSaving(false);
        }
    };

    /**
     * Close drawer
     */
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setEditingIncidentId(null);
        // Reset form data when drawer closes
        setFormData({
            applicationId: "",
            configurationItem: "",
            shortDescription: "",
            priority: "",
            mvc: "",
            platform: "",
            portfolio: "",
            causedByPlatform: "",
            causedByPortfolio: "",
            brand: "",
            causedByChange: "",
            changeType: "",
            changeProcess: "",
            triggeringChangeRequest: "",
            observations: "",
            rootCauseCode: "",
            rootCauseSubCode: ""
        });
    };

    // Memoize unique values for all columns to prevent recalculation
    const uniqueColumnValues = useMemo(() => {
        if (!p1p2IncidentsData) return {};
        
        const allColumns = [
            'id', 'application_id', 'configuration_item', 'priority', 'brand', 'mvc',
            'platform', 'portfolio', 'caused_by_platform', 'caused_by_portfolio', 'caused_by_change',
            'change_type', 'change_process', 'triggering_change_request', 'root_cause_code', 'root_cause_sub_code'
        ];
        
        const values = {};
        allColumns.forEach(col => {
            values[col] = getUniqueColumnValues(p1p2IncidentsData, col);
        });
        return values;
    }, [p1p2IncidentsData]);

    /**
     * Render a filter header for a column
     */
    const renderColumnFilter = (columnId, label, dataKey) => {
        const uniqueValues = uniqueColumnValues[dataKey] || [];
        const selectedValues = columnFilters[dataKey] || [];
        const isFiltered = selectedValues.length > 0;

        return (
            <div 
                slot={`${columnId}_header`} 
                style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <span>{label}</span>
                <McPopover 
                    position="bottom-right" 
                    trigger="click"
                    click={(e) => e.stopPropagation()}
                >
                    <McButton
                        appearance="neutral"
                        fit="small"
                        hiddenlabel={true}
                        icon={isFiltered ? "funnel-solid" : "funnel"}
                        label="Show filter menu"
                        padding="compact"
                        slot="trigger"
                        variant="plain"
                        click={(e) => e.stopPropagation()}
                    />
                    <div 
                        style={{ padding: '16px 12px', maxHeight: '300px', overflowY: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <McCheckboxGroup
                            legend={label}
                            orientation="vertical"
                            hiddenlegend
                            change={(e) => {
                                e.stopPropagation();
                                const newSelectedValues = e.detail || [];
                                handleColumnFilterChange(dataKey, newSelectedValues);
                            }}
                            click={(e) => e.stopPropagation()}
                        >
                            {uniqueValues.map((value, index) => (
                                <McCheckbox
                                    key={`${columnId}-${index}`}
                                    label={value}
                                    value={value}
                                    checked={selectedValues.includes(value)}
                                    click={(e) => e.stopPropagation()}
                                />
                            ))}
                        </McCheckboxGroup>
                    </div>
                </McPopover>
            </div>
        );
    };

    /**
     * Downloads the filtered data as a CSV file
     */
    const downloadCSV = () => {
        if (!filteredData || filteredData.length === 0) {
            toast.error("No data available to download", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }

        const csvData = filteredData.map(incident => transformIncidentForCSV(incident, activeP1P2Tab));
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, `p1p2_incidents_${DateTime.now().toFormat('yyyy-MM-dd_HH-mm-ss')}.csv`);
    };

    return (
        <div className="home-table-container" style={{ marginTop: "10px" }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    position: 'relative',
                }}
            >
                <div style={{ flex: '0 0 auto' }}>
                    <FilterButton
                        columns={filterColumns}
                        data={p1p2IncidentsData || []}
                        onApply={handleFilterApply}
                        activeFilters={activeFilters}
                        setActiveFilters={setActiveFilters}
                    />
                </div>

                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                        width: 'max-content',
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{ fontWeight: "600", fontSize: '1.2rem', color: "#555" }}>
                        {headingText}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: '0 0 auto' }}>
                    {!props.isLoading && 
                        <span style={{ fontSize: '1rem', color: '#0077b2' }}>
                            Total: {filteredData.length}
                        </span>
                    }
                    <McButton
                        label="Edit"
                        appearance="primary"
                        variant="outlined"
                        fit="small"
                        click={handleEditClick}
                        disabled={selectedRows.length === 0}
                    >
                        <span slot="leadingicon">
                            <mc-icon class="my-icon" icon="pencil-simple"></mc-icon>
                        </span>
                    </McButton>
                    {props.onRefresh && (
                        <McButton
                            label="Refresh"
                            appearance="primary"
                            variant="outlined"
                            fit="small"
                            click={props.onRefresh}
                        >
                        </McButton>
                    )}
                    <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={downloadCSV}
                        disabled={!filteredData || filteredData.length === 0}
                    >
                        <span slot="trailingicon">
                            <mc-icon class="my-icon" icon="tray-arrow-down" color="white"></mc-icon>
                        </span>
                    </McButton>
                </div>
            </div>

            <McTable
                columns={tableColumns}
                data={transformedData}
                datakey="incidentNumber"
                customstyles={tableStyles}
                headersticky
                fit="small"
                className="custom-table"
                select
                selectlabel="Select row"
                selected={selectedRows}
                selectchange={handleSelectChange}
                stateslotheight="calc(65vh - 60px)"
                sortdisableoninitialload
            >
                {props.isLoading ? (
                    <div slot="state" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(65vh - 60px)', width: '100%', position: 'relative' }}>
                        <div style={{ zIndex: 9999 }}>
                            <mc-loading-indicator></mc-loading-indicator>
                        </div>
                    </div>
                ) : (
                    transformedData?.length === 0 && (
                        <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", fontWeight: "600", width: '100%' }}>
                            No Data Found
                        </div>
                    )
                )}
                {/* Header filters for all columns except description, opened, and resolved */}
                {renderColumnFilter('incidentNumber', props.Header1 || 'Incident Number', 'id')}
                {renderColumnFilter('applicationId', props.Header2 || 'Application ID', 'application_id')}
                {renderColumnFilter('configurationItem', props.Header3 || 'Configuration Item', 'configuration_item')}
                {renderColumnFilter('priority', props.Header7 || 'Priority', 'priority')}
                {renderColumnFilter('brand', props.Header8 || 'Brand', 'brand')}
                {renderColumnFilter('mvc', props.Header9 || 'MVC', 'mvc')}
                {renderColumnFilter('platform', props.Header10 || 'Platform', 'platform')}
                {renderColumnFilter('portfolio', props.Header11 || 'Portfolio', 'portfolio')}
                {renderColumnFilter('causedByPlatform', props.Header12 || 'Caused By Platform', 'caused_by_platform')}
                {renderColumnFilter('causedByPortfolio', props.Header13 || 'Caused By Portfolio', 'caused_by_portfolio')}
                {renderColumnFilter('causedByChange', props.Header14 || 'Caused By Change', 'caused_by_change')}
                
                {/* Additional filters for P1/P2 due to change tab */}
                {activeP1P2Tab === "P1/P2 due to change" && (
                    <>
                        {renderColumnFilter('changeType', props.Header15 || 'Change Type', 'change_type')}
                        {renderColumnFilter('changeProcess', props.Header16 || 'Change Process', 'change_process')}
                        {renderColumnFilter('triggeringChangeRequest', props.Header17 || 'Triggering Change Request', 'triggering_change_request')}
                        {renderColumnFilter('rootCauseCode', props.Header19 || 'Root Cause Code', 'root_cause_code')}
                        {renderColumnFilter('rootCauseSubCode', props.Header20 || 'Root Cause Sub Code', 'root_cause_sub_code')}
                    </>
                )}
            </McTable>

            {/* Pagination */}
            {!props.isLoading && filteredData.length > recordsPerPage && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <McPagination
                        pagechange={handlePageChange}
                        currentpage={currentPage}
                        totalpages={totalPages}
                    />
                </div>
            )}

            {/* Edit Drawer */}
            <McDrawer
                open={isDrawerOpen}
                dimension="medium"
                position="right"
                closed={handleDrawerClose}
                key={editingIncidentId}
            >
                <span slot="heading" style={{ color: "#0073ab" }}>Edit Incident</span>
                
                <div>
                    {/* Header info */}
                    <div style={{ marginBottom: "20px", padding: "12px", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                        <div style={{ fontSize: "14px", color: "#666" }}>
                            Editing data for incident: <strong>{selectedRows[0]?.incidentNumber || "N/A"}</strong>
                        </div>
    
                    </div>

                    {/* Form fields */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    
                    {/* Application ID - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Application ID
                        </label>
                        <McInput
                            key={`application-id-${editingIncidentId}`}
                            label=""
                            type="text"
                            name="application_id"
                            value={formData.applicationId}
                            input={(e) => handleFormChange('applicationId', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter application ID"
                            fit="small"
                        />
                    </div>

                    {/* Configuration Item - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Configuration Item
                        </label>
                        <McInput
                            key={`configuration-item-${editingIncidentId}`}
                            label=""
                            type="text"
                            name="configuration_item"
                            value={formData.configurationItem}
                            input={(e) => handleFormChange('configurationItem', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter configuration item"
                            fit="small"
                        />
                    </div>

                    {/* Short Description - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Short Description
                        </label>
                        <McTextarea
                            key={`short-description-${editingIncidentId}`}
                            label=""
                            name="short_description"
                            value={formData.shortDescription}
                            input={(e) => handleFormChange('shortDescription', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter short description"
                            rows={3}
                            fit="small"
                        />
                    </div>

                    {/* Priority - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Priority
                        </label>
                        <McSelect
                            key={`priority-${editingIncidentId}`}
                            label=""
                            name="priority"
                            value={formData.priority}
                            optionselected={(e) => {
                                const selectedValue = e.detail?.value || e.detail || "";
                                handleFormChange('priority', selectedValue);
                            }}
                            style={{ width: "100%" }}
                            fit="small"
                        >
                            <McOption value="P1" selected={formData.priority === "P1"}>P1</McOption>
                            <McOption value="P2" selected={formData.priority === "P2"}>P2</McOption>
                        </McSelect>
                    </div>

                    {/* MVC - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            MVC
                        </label>
                        <McSelect
                            key={`mvc-${editingIncidentId}`}
                            name="edit_mvc_select"
                            label=""
                            value={formData.mvc}
                            optionselected={(e) => {
                                const selectedValue = e.detail?.value || e.detail || "";
                                handleFormChange('mvc', selectedValue);
                            }}
                            fit="small"
                        >
                            <McOption value="Yes" selected={formData.mvc === "Yes"}>Yes</McOption>
                            <McOption value="No" selected={formData.mvc === "No"}>No</McOption>
                        </McSelect>
                    </div>

                    {/* Platform - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Platform
                        </label>
                        <McInput
                            key={`platform-${editingIncidentId}`}
                            label=""
                            type="text"
                            name="platform"
                            value={formData.platform}
                            input={(e) => handleFormChange('platform', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter platform"
                            fit="small"
                        />
                    </div>

                    {/* Portfolio - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Portfolio
                        </label>
                        <McInput
                            key={`portfolio-${editingIncidentId}`}
                            label=""
                            type="text"
                            name="portfolio"
                            value={formData.portfolio}
                            input={(e) => handleFormChange('portfolio', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter portfolio"
                            fit="small"
                        />
                    </div>

                    {/* Caused By Platform - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Caused By Platform
                        </label>
                        <McInput
                            key={`caused-by-platform-${editingIncidentId}`}
                            label=""
                            type="text"
                            name="caused_by_platform"
                            value={formData.causedByPlatform}
                            input={(e) => handleFormChange('causedByPlatform', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter caused by platform"
                            fit="small"
                        />
                    </div>

                    {/* Caused By Portfolio - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Caused By Portfolio
                        </label>
                        <McInput
                            key={`caused-by-portfolio-${editingIncidentId}`}
                            label=""
                            type="text"
                            name="caused_by_portfolio"
                            value={formData.causedByPortfolio}
                            input={(e) => handleFormChange('causedByPortfolio', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter caused by portfolio"
                            fit="small"
                        />
                    </div>

                    {/* Brand - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Brand
                        </label>
                        <McInput
                            key={`brand-${editingIncidentId}`}
                            label=""
                            type="text"
                            name="brand"
                            value={formData.brand}
                            input={(e) => handleFormChange('brand', e.target.value)}
                            style={{ width: "100%" }}
                            placeholder="Enter brand"
                            fit="small"
                        />
                    </div>

                    {/* Caused By Change - Always shown */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Caused By Change
                        </label>
                        <McSelect
                            key={`caused-by-change-${editingIncidentId}`}
                            name="edit_caused_by_change_select"
                            label=""
                            value={formData.causedByChange}
                            optionselected={(e) => {
                                console.log("Caused by change option selected:", e.detail);
                                const selectedValue = e.detail?.value || e.detail || "";
                                handleFormChange('causedByChange', selectedValue);
                            }}
                            fit="small"
                        >
                            <McOption value="Yes" selected={formData.causedByChange === "Yes"}>Yes</McOption>
                            <McOption value="No" selected={formData.causedByChange === "No"}>No</McOption>
                        </McSelect>
                    </div>

                    {/* Additional fields for P1/P2 due to change tab */}
                    {activeP1P2Tab === "P1/P2 due to change" && (
                        <>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Change Process
                                </label>
                                <McSelect
                                    key={`change-process-${editingIncidentId}`}
                                    placeholder="Select change process"
                                    name="edit_change_process_select"
                                    label=""
                                    value={formData.changeProcess}
                                    optionselected={(e) => {
                                        console.log("Change process option selected:", e.detail);
                                        const selectedValue = e.detail?.value || e.detail || "";
                                        handleFormChange('changeProcess', selectedValue);
                                    }}
                                    fit="small"
                                >
                                    <McOption value="Automated" selected={formData.changeProcess === "Automated"}>Automated</McOption>
                                    <McOption value="Manual" selected={formData.changeProcess === "Manual"}>Manual</McOption>
                                </McSelect>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Change Type
                                </label>
                                <McInput
                                    key={`change-type-${editingIncidentId}`}
                                    label=""
                                    type="text"
                                    name="change_type"
                                    value={formData.changeType}
                                    input={(e) => handleFormChange('changeType', e.target.value)}
                                    style={{ width: "100%" }}
                                    placeholder="Enter change type"
                                    fit="small"
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                                    <label style={{ fontWeight: '600', margin: 0}}>
                                        Triggering Change Request
                                    </label>
                                    <span style={{fontSize:'15px'}}> 
                                        (In case of multiple, separate IDs with commas)
                                    </span>
                                </div>
                                <McInput
                                    key={`triggering-change-request-${editingIncidentId}`}
                                    label=""
                                    type="text"
                                    name="triggering_change_request"
                                    value={formData.triggeringChangeRequest}
                                    input={(e) => handleFormChange('triggeringChangeRequest', e.target.value)}
                                    style={{ width: "100%" }}
                                    placeholder="Enter change request IDs (comma-separated if multiple: e.g., CHG1234567, CHG1212121)"
                                    fit="small"
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Root Cause Code
                                </label>
                                <McInput
                                    key={`root-cause-code-${editingIncidentId}`}
                                    label=""
                                    type="text"
                                    name="root_cause_code"
                                    value={formData.rootCauseCode}
                                    input={(e) => handleFormChange('rootCauseCode', e.target.value)}
                                    style={{ width: "100%" }}
                                    placeholder="Enter root cause code"
                                    fit="small"
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Root Cause Sub Code
                                </label>
                                <McInput
                                    key={`root-cause-sub-code-${editingIncidentId}`}
                                    label=""
                                    type="text"
                                    name="root_cause_sub_code"
                                    value={formData.rootCauseSubCode}
                                    input={(e) => handleFormChange('rootCauseSubCode', e.target.value)}
                                    style={{ width: "100%" }}
                                    placeholder="Enter root cause sub code"
                                    fit="small"
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Observations
                                </label>
                                <McTextarea
                                    key={`observations-${editingIncidentId}`}
                                    label=""
                                    name="observations"
                                    value={formData.observations}
                                    input={(e) => handleFormChange('observations', e.target.value)}
                                    style={{ width: "100%" }}
                                    placeholder="Enter observations"
                                    rows={4}
                                    fit="small"
                                />
                            </div>
                        </>
                    )}
                    </div>
                </div>

                <div slot="footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <McButton
                        label="Cancel"
                        appearance="neutral"
                        variant="outlined"
                        fit="medium"
                        click={handleDrawerClose}
                        disabled={isSaving}
                    />
                    <McButton
                        label={isSaving ? "Saving..." : "Save"}
                        appearance="primary"
                        variant="filled"
                        fit="medium"
                        click={handleSave}
                        disabled={isSaving}
                    />
                </div>
            </McDrawer>

            <div style={{ marginTop: "8vh", visibility: "hidden" }}>
                ""
            </div>
        </div>
    );
}
